/**
 * TransactionLogContent — unified log table for Sales and Purchase records.
 *
 * Layout:
 *   - Compact summary strip (replaces 3 clunky tiles)
 *   - 3-level expandable table:
 *       Level 1 — Day row (date, bill count, total) — collapsed by default
 *       Level 2 — Bill row (bill no, customer/supplier, payment, total)
 *       Level 3 — Item rows (product, qty, price, item total)
 *
 * Props:
 *   type          'sales' | 'purchase'
 *   onNeedRefresh ref callback for parent-triggered refresh
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useIsMobile } from '@/hooks/useIsMobile';
import { salesAPI, purchasesAPI, incomingInvoicesAPI } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ScrollText, ShoppingCart, Download, RefreshCw,
  ChevronUp, ChevronDown, Search, IndianRupee,
  Trash2, RotateCcw, Pencil, Loader2, X, Check, PackagePlus, History, Truck,
} from 'lucide-react';
import DateRangeFilter from '@/components/DateRangeFilter';
import { exportCSV } from '@/lib/csvExport';
import { toast } from 'sonner';

const PAGE_SIZE = 300; // large enough that most date ranges fit without pagination

const PAYMENT_COLORS = {
  cash:   'bg-emerald-100 text-emerald-700 border-emerald-200',
  card:   'bg-blue-100   text-blue-700   border-blue-200',
  upi:    'bg-violet-100 text-violet-700 border-violet-200',
  khata:  'bg-orange-100 text-orange-700 border-orange-200',
  cheque: 'bg-slate-100  text-slate-600  border-slate-200',
};

const CONFIG = {
  sales: {
    Icon:           ScrollText,
    title:          'Sales Log',
    subtitle:       'Full history of all transactions',
    loadingText:    'Loading sales…',
    emptyText:      'No sales found for this date range',
    accentColor:    'emerald',
    itemAccent:     'emerald',
    countLabel:     'bills',
    thirdColHeader: 'Customer',
    thirdColSort:   'customer',
    dateField:      (r) => r.created_at,
    thirdColValue:  (r) => r.customer_name || null,
    thirdColFallback: 'Walk-in',
    searchFields:   (r, q) =>
      (r.bill_number || '').toLowerCase().includes(q) ||
      (r.customer_name || '').toLowerCase().includes(q) ||
      (r.payment_mode || '').toLowerCase().includes(q),
    fetchFn:        (params) => salesAPI.getAll(params),
    exportHeaders:  ['Date', 'Bill No', 'Customer', 'Payment Mode', 'Items', 'GST', 'Total'],
    exportRow:      (r) => [
      r.created_at?.slice(0, 10) || '',
      r.bill_number || r.id || '',
      r.customer_name || 'Walk-in',
      r.payment_mode || '',
      (r.items || []).map(i => `${i.product_name || i.name || 'Item'} x${i.quantity}`).join('; '),
      r.total_gst?.toFixed(2) || '0',
      r.total_amount?.toFixed(2) || '0',
    ],
    exportFilter:   (rows) => rows.filter(r => r.status !== 'deleted'),
    totalAmount:    (rows) => rows.filter(x => x.status !== 'deleted').reduce((s, x) => s + (x.total_amount || 0), 0),
  },
  purchase: {
    Icon:           ShoppingCart,
    title:          'Purchase Log',
    subtitle:       'All goods received from suppliers',
    loadingText:    'Loading purchases…',
    emptyText:      'No purchases found for this date range',
    accentColor:    'orange',
    itemAccent:     'orange',
    countLabel:     'purchases',
    thirdColHeader: 'Supplier',
    thirdColSort:   'supplier',
    dateField:      (r) => r.purchase_date,
    thirdColValue:  (r) => r.supplier_name || null,
    thirdColFallback: 'Unknown',
    searchFields:   (r, q) =>
      (r.bill_number || '').toLowerCase().includes(q) ||
      (r.supplier_name || '').toLowerCase().includes(q) ||
      (r.payment_mode || '').toLowerCase().includes(q),
    fetchFn:        (params) => purchasesAPI.getAll(params),
    exportHeaders:  ['Date', 'Bill No', 'Supplier', 'Items', 'Payment Mode', 'GST', 'Total'],
    exportRow:      (r) => [
      r.purchase_date?.slice(0, 10) || '',
      r.bill_number || r.id || '',
      r.supplier_name || 'Unknown',
      (r.items || []).map(i => `${i.product_name || 'Item'} x${i.quantity}`).join('; '),
      r.payment_mode || '',
      r.total_gst?.toFixed(2) || '0',
      r.total_amount?.toFixed(2) || '0',
    ],
    exportFilter:   (rows) => rows.filter(r => r.status !== 'deleted'),
    totalAmount:    (rows) => rows.filter(x => x.status !== 'deleted').reduce((s, x) => s + (x.total_amount || 0), 0),
  },
};

// ── helpers ───────────────────────────────────────────────────────────────────
const dateKey = (record, cfg) => {
  const raw = cfg.dateField(record);
  if (!raw) return 'unknown';
  return raw.slice(0, 10); // YYYY-MM-DD
};

const formatDate = (isoDate) => {
  if (!isoDate || isoDate === 'unknown') return '—';
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtAmount = (n) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Group records by date, return sorted descending
const groupByDate = (records, cfg) => {
  const map = {};
  for (const r of records) {
    const d = dateKey(r, cfg);
    if (!map[d]) map[d] = [];
    map[d].push(r);
  }
  return Object.entries(map).sort(([a], [b]) => (a < b ? 1 : -1));
};

// ── Inline edit form for purchase rows ───────────────────────────────────────
const PurchaseInlineEdit = ({ record, onSave, onCancel }) => {
  const [draft, setDraft] = useState({
    supplier_name: record.supplier_name || '',
    purchase_date: record.purchase_date || '',
    payment_mode:  record.payment_mode  || 'cash',
    items: (record.items || []).map(i => ({
      catalog_id:   i.catalog_id,
      product_name: i.product_name,
      quantity:     i.quantity,
      cost_price:   i.cost_price,
    })),
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const setItem = (idx, k, v) =>
    setDraft(d => ({ ...d, items: d.items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await purchasesAPI.updatePurchase(record.id, {
        ...draft,
        items: draft.items.map(i => ({ ...i, quantity: +i.quantity, cost_price: +i.cost_price })),
      });
      toast.success('Purchase updated');
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update purchase');
    } finally {
      setSaving(false);
    }
  };

  const total = draft.items.reduce((s, i) => s + (+i.quantity || 0) * (+i.cost_price || 0), 0);

  return (
    <div className="bg-amber-50/60 border-t border-amber-200 px-8 py-4 space-y-4">
      {/* Metadata row */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Supplier</label>
          <input
            value={draft.supplier_name}
            onChange={e => set('supplier_name', e.target.value)}
            className="w-full text-sm border border-border rounded-lg px-2.5 py-1.5 outline-none focus:border-primary bg-white"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Stock Received Date</label>
          <input
            type="date"
            value={draft.purchase_date}
            onChange={e => set('purchase_date', e.target.value)}
            className="w-full text-sm border border-border rounded-lg px-2.5 py-1.5 outline-none focus:border-primary bg-white"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Payment Mode</label>
          <select
            value={draft.payment_mode}
            onChange={e => set('payment_mode', e.target.value)}
            className="w-full text-sm border border-border rounded-lg px-2.5 py-1.5 outline-none focus:border-primary bg-white"
          >
            {['cash', 'card', 'upi', 'cheque', 'credit'].map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Items table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-muted/50 text-muted-foreground border-b border-border">
              <th className="text-left px-3 py-2 font-medium">Product</th>
              <th className="text-right px-3 py-2 font-medium w-24">Qty</th>
              <th className="text-right px-3 py-2 font-medium w-28">Cost Price ₹</th>
              <th className="text-right px-3 py-2 font-medium w-24">Total ₹</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-white">
            {draft.items.map((item, idx) => (
              <tr key={item.catalog_id}>
                <td className="px-3 py-2 text-slate-700">{item.product_name || item.catalog_id}</td>
                <td className="px-3 py-2">
                  <input
                    type="number" min="0.01" step="any"
                    value={item.quantity}
                    onChange={e => setItem(idx, 'quantity', e.target.value)}
                    className="w-20 text-sm border border-border rounded px-1.5 py-0.5 outline-none focus:border-primary text-right ml-auto block"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number" min="0" step="any"
                    value={item.cost_price}
                    onChange={e => setItem(idx, 'cost_price', e.target.value)}
                    className="w-24 text-sm border border-border rounded px-1.5 py-0.5 outline-none focus:border-primary text-right ml-auto block"
                  />
                </td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-orange-700">
                  ₹{((+item.quantity || 0) * (+item.cost_price || 0)).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">New Total: ₹{fmtAmount(total)}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>
            <X className="h-3.5 w-3.5 mr-1" /> Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const TransactionLogContent = ({ type, onNeedRefresh }) => {
  const cfg = CONFIG[type];
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [records,       setRecords]       = useState([]);
  const [total,         setTotal]         = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [page,          setPage]          = useState(0);
  const [searchTerm,    setSearchTerm]    = useState('');
  const [expandedDays,  setExpandedDays]  = useState(new Set());
  const [expandedBills, setExpandedBills] = useState(new Set());
  const [showDeleted,   setShowDeleted]   = useState(false);
  const [editingId,     setEditingId]     = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    end:   new Date().toISOString().split('T')[0],
  });

  const { data: incomingCountData } = useQuery({
    queryKey: ['incoming-invoice-count'],
    queryFn: () => incomingInvoicesAPI.getCount(),
    select: r => r.data?.count ?? 0,
    enabled: type === 'purchase',
    staleTime: 30_000,
  });
  const incomingCount = incomingCountData ?? 0;

  const fetchRecords = useCallback(async (pg = 0) => {
    setLoading(true);
    try {
      const params = {
        start_date: dateRange.start,
        end_date:   dateRange.end,
        skip:       pg * PAGE_SIZE,
        limit:      PAGE_SIZE,
      };
      if (type === 'purchase' && showDeleted) {
        params.include_deleted = true;
      }
      const res = await cfg.fetchFn(params);
      const d = res.data;
      setRecords(d.items || d.purchases || (Array.isArray(d) ? d : []));
      setTotal(d.total || (d.items || d.purchases || []).length);
    } catch (e) {
      console.error(`Failed to fetch ${type}:`, e);
    } finally {
      setLoading(false);
    }
  }, [dateRange, cfg, type, showDeleted]);

  useEffect(() => { setPage(0); setExpandedDays(new Set()); setExpandedBills(new Set()); }, [dateRange, showDeleted]);
  useEffect(() => { fetchRecords(page); }, [page, dateRange, showDeleted]);
  useEffect(() => {
    if (onNeedRefresh) onNeedRefresh.current = () => { fetchRecords(0); };
  }, [fetchRecords, onNeedRefresh]);

  const handleExportCSV = async () => {
    try {
      const res = await cfg.fetchFn({ start_date: dateRange.start, end_date: dateRange.end, limit: 5000, skip: 0 });
      const rows = cfg.exportFilter(res.data?.items || res.data?.purchases || res.data || []);
      exportCSV(
        cfg.exportHeaders,
        rows.map(cfg.exportRow),
        `${type}-log-${dateRange.start}-to-${dateRange.end}.csv`,
      );
    } catch (e) {
      console.error('Export failed:', e);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Delete this purchase (${record.bill_number || record.id})? Stock will be reduced.`)) return;
    try {
      await purchasesAPI.deletePurchase(record.id);
      toast.success('Purchase deleted and stock adjusted');
      fetchRecords(page);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete purchase');
    }
  };

  const handleRestore = async (record) => {
    try {
      await purchasesAPI.restorePurchase(record.id);
      toast.success('Purchase restored and stock re-added');
      fetchRecords(page);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore purchase');
    }
  };

  const totalPages  = Math.ceil(total / PAGE_SIZE);
  const totalAmount = cfg.totalAmount(records);

  const q = searchTerm.toLowerCase();
  const filtered = q
    ? records.filter(r => !q || cfg.searchFields(r, q))
    : records;

  const grouped = groupByDate(filtered, cfg);

  useEffect(() => {
    if (q) {
      setExpandedDays(new Set(grouped.map(([day]) => day)));
      setExpandedBills(new Set(filtered.map(r => r.id || '')));
    } else {
      setExpandedDays(new Set());
      setExpandedBills(new Set());
    }
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDay  = (d)  => setExpandedDays(prev  => { const s = new Set(prev); s.has(d)  ? s.delete(d)  : s.add(d);  return s; });
  const toggleBill = (id) => setExpandedBills(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const { Icon, accentColor, itemAccent } = cfg;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Mobile compact header — purchase only */}
      {isMobile && type === 'purchase' && (
        <div className="flex-none bg-card border-b border-border">
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <h1 className="text-base font-semibold text-foreground">Purchase</h1>
            <div className="inline-flex items-center gap-0.5 bg-muted rounded-lg p-0.5 shrink-0">
              <button
                onClick={() => navigate('/purchases')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <PackagePlus className="w-3 h-3 shrink-0" /> Record
              </button>
              <button className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all bg-background text-foreground shadow-sm whitespace-nowrap">
                <History className="w-3 h-3 shrink-0" /> History
              </button>
              <button
                onClick={() => navigate('/purchases?view=incoming')}
                className="relative flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <Truck className="w-3 h-3 shrink-0" /> Incoming
                {incomingCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 flex items-center justify-center text-[9px] font-bold rounded-full bg-amber-500 text-white px-0.5 leading-none">
                    {incomingCount}
                  </span>
                )}
              </button>
            </div>
          </div>
          {/* Condensed summary + actions */}
          <div className="flex items-center justify-between px-4 pb-2 gap-2">
            <div className="flex items-center gap-2 text-sm">
              <IndianRupee className={`h-3.5 w-3.5 text-${accentColor}-600 shrink-0`} />
              <span className={`font-bold text-${accentColor}-700 font-mono`}>
                ₹{totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </span>
              <span className="text-muted-foreground text-xs">· {total} {cfg.countLabel}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => fetchRecords(page)} disabled={loading}
                className="p-1.5 rounded-lg border border-border hover:bg-muted/60 text-muted-foreground">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={handleExportCSV}
                className="p-1.5 rounded-lg border border-border hover:bg-muted/60 text-muted-foreground">
                <Download className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed header — desktop (and mobile non-purchase) */}
      {!(isMobile && type === 'purchase') && (
      <div className="flex-none flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-border bg-white">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Icon className="h-6 w-6 text-primary" />
              {cfg.title}
            </h2>
            <p className="text-muted-foreground mt-0.5 text-sm">{cfg.subtitle}</p>
          </div>
          {/* Compact summary strip */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm">
            <IndianRupee className={`h-3.5 w-3.5 text-${accentColor}-600`} />
            <span className={`font-bold text-${accentColor}-700 font-mono`}>
              ₹{totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </span>
            <span className="text-slate-300">·</span>
            <span className="text-slate-600">{total.toLocaleString('en-IN')} {cfg.countLabel}</span>
            {totalPages > 1 && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-slate-400 text-xs">pg {page + 1}/{totalPages}</span>
              </>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchRecords(page)} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={handleExportCSV} className="gap-1.5">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
        {type === 'purchase' && (
          <div className="inline-flex items-center gap-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => navigate('/purchases')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground"
            >
              <PackagePlus className="w-3.5 h-3.5" />
              Record Purchase
            </button>
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all bg-background text-foreground shadow-sm">
              <History className="w-3.5 h-3.5" />
              Purchase History
            </button>
            <button
              onClick={() => navigate('/purchases?view=incoming')}
              className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground"
            >
              <Truck className="w-3.5 h-3.5" />
              Incoming Invoices
              {incomingCount > 0 && (
                <span className="ml-1 min-w-[18px] h-4 inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-amber-500 text-white px-1 leading-none">
                  {incomingCount}
                </span>
              )}
            </button>
          </div>
        )}
      </div>
      )}

      {/* Scrollable body */}
      <div className="flex-1 overflow-auto p-6 space-y-3">
      {/* Date Range Filter — inline, no card */}
      <DateRangeFilter dateRange={dateRange} onChange={setDateRange} />

      {/* Filters row */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Search */}
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder={`Search bill no, ${cfg.thirdColHeader.toLowerCase()}, payment…`}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-primary bg-white"
          />
        </div>
        {/* Show deleted toggle — purchases only */}
        {type === 'purchase' && (
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={e => setShowDeleted(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 accent-orange-500"
            />
            Show deleted
          </label>
        )}
      </div>

      {/* 3-level expandable table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <RefreshCw className="h-6 w-6 animate-spin mr-2" /> {cfg.loadingText}
          </div>
        ) : grouped.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <Icon className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">{cfg.emptyText}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {grouped.map(([day, dayRecords]) => {
              const dayTotal   = dayRecords.filter(r => r.status !== 'deleted').reduce((s, r) => s + (r.total_amount || 0), 0);
              const billCount  = dayRecords.length;
              const isDayOpen  = expandedDays.has(day);

              return (
                <div key={day}>
                  {/* ── Level 1: Day row ─────────────────────────────── */}
                  <button
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                    onClick={() => toggleDay(day)}
                  >
                    {isDayOpen
                      ? <ChevronUp   className="h-4 w-4 text-slate-400 shrink-0" />
                      : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                    <span className="font-semibold text-slate-700 text-sm min-w-[130px]">
                      {formatDate(day)}
                    </span>
                    <span className="text-xs text-slate-400 ml-1">
                      {billCount} {billCount === 1 ? cfg.countLabel.replace(/s$/, '') : cfg.countLabel}
                    </span>
                    <span className="ml-auto font-bold font-mono text-sm text-slate-700">
                      ₹{fmtAmount(dayTotal)}
                    </span>
                  </button>

                  {/* ── Level 2: Bill rows (when day expanded) ────────── */}
                  {isDayOpen && dayRecords.map((record, bi) => {
                    const items         = record.items || [];
                    const billId        = record.id || `${day}-${bi}`;
                    const isBillOpen    = expandedBills.has(billId);
                    const thirdVal      = cfg.thirdColValue(record);
                    const isDeleted     = record.status === 'deleted';
                    const isAmended     = type === 'sales' && record.status === 'amended';
                    const isDimmed      = isDeleted || isAmended;
                    const isEditingThis = type === 'purchase' && editingId === billId;

                    // Date+time display for sales
                    const dateVal = cfg.dateField(record);
                    const dt      = dateVal ? new Date(dateVal) : null;
                    const timeStr = type === 'sales' && dt
                      ? dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
                      : '';

                    return (
                      <React.Fragment key={billId}>
                        {/* Bill row */}
                        <div
                          className={`w-full flex items-center gap-3 px-6 py-2.5 text-left transition-colors ${
                            isDimmed
                              ? 'opacity-50'
                              : isEditingThis
                              ? `bg-${itemAccent}-50/60`
                              : `hover:bg-${itemAccent}-50/40 cursor-pointer`
                          }`}
                          onClick={() => !isEditingThis && toggleBill(billId)}
                        >
                          {isBillOpen && !isEditingThis
                            ? <ChevronUp   className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                            : <ChevronDown className="h-3.5 w-3.5 text-slate-300 shrink-0" />}

                          {/* Bill no */}
                          <span className={`font-mono text-xs text-slate-500 min-w-[100px] ${isDimmed ? 'line-through' : ''}`}>
                            {record.bill_number || record.id?.slice(-6) || '—'}
                            {isAmended && <span className="ml-1 text-[10px] text-amber-600 not-italic font-semibold">✦ Amended</span>}
                            {isDeleted && <span className="ml-1 text-[10px] text-red-500 not-italic font-semibold">✗ Deleted</span>}
                          </span>

                          {/* Time (sales only) */}
                          {timeStr && (
                            <span className="text-xs text-slate-400 min-w-[45px]">{timeStr}</span>
                          )}

                          {/* Customer / Supplier */}
                          <span className={`text-sm text-slate-600 flex-1 truncate ${isDimmed ? 'line-through' : ''}`}>
                            {thirdVal || <span className="text-slate-400 italic">{cfg.thirdColFallback}</span>}
                          </span>

                          {/* Items count */}
                          <span className="text-xs text-slate-400 shrink-0">
                            {items.length} item{items.length !== 1 ? 's' : ''}
                          </span>

                          {/* Payment badge */}
                          {record.payment_mode && (
                            <Badge variant="outline" className={`text-[10px] shrink-0 ${PAYMENT_COLORS[record.payment_mode] || 'bg-slate-100 text-slate-600'}`}>
                              {record.payment_mode}
                            </Badge>
                          )}

                          {/* Bill total */}
                          <span className={`font-mono text-sm font-semibold shrink-0 ml-2 ${
                            isDimmed ? 'text-slate-400 line-through' : `text-${itemAccent}-700`
                          }`}>
                            ₹{fmtAmount(record.total_amount)}
                          </span>

                          {/* Purchase action buttons */}
                          {type === 'purchase' && (
                            <div
                              className="flex gap-1 shrink-0 ml-1"
                              onClick={e => e.stopPropagation()}
                            >
                              {isDeleted ? (
                                <button
                                  title="Restore purchase"
                                  onClick={() => handleRestore(record)}
                                  className="p-1.5 rounded text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                                >
                                  <RotateCcw className="h-3.5 w-3.5" />
                                </button>
                              ) : (
                                <>
                                  <button
                                    title="Edit purchase"
                                    onClick={() => {
                                      if (isEditingThis) { setEditingId(null); } else { setEditingId(billId); setExpandedBills(prev => { const s = new Set(prev); s.delete(billId); return s; }); }
                                    }}
                                    className={`p-1.5 rounded transition-colors ${isEditingThis ? 'text-primary bg-primary/10' : 'text-slate-400 hover:text-primary hover:bg-primary/5'}`}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    title="Delete purchase"
                                    onClick={() => handleDelete(record)}
                                    className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>

                        {/* ── Inline edit panel (purchase only) ─── */}
                        {isEditingThis && (
                          <PurchaseInlineEdit
                            record={record}
                            onSave={() => { setEditingId(null); fetchRecords(page); }}
                            onCancel={() => setEditingId(null)}
                          />
                        )}

                        {/* ── Level 3: Item rows (when bill expanded, not editing) ─── */}
                        {isBillOpen && !isEditingThis && items.length > 0 && (
                          <div className={`bg-${itemAccent}-50/50 px-8 pb-3 pt-1 border-t border-${itemAccent}-100/60`}>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className={`text-slate-500 border-b border-${itemAccent}-100`}>
                                  <th className="text-left py-1.5 pr-4 font-medium">Product</th>
                                  <th className="text-right py-1.5 pr-4 font-medium">Qty</th>
                                  <th className="text-right py-1.5 pr-4 font-medium">
                                    {type === 'sales' ? 'Unit Price' : 'Cost'} ₹
                                  </th>
                                  <th className="text-right py-1.5 font-medium">Total ₹</th>
                                </tr>
                              </thead>
                              <tbody className={`divide-y divide-${itemAccent}-100/60`}>
                                {items.map((item, j) => {
                                  const unitPrice = type === 'sales'
                                    ? (item.unit_price ?? item.price ?? null)
                                    : (item.cost_price ?? null);
                                  return (
                                    <tr key={j} className="text-slate-700">
                                      <td className="py-1.5 pr-4">{item.product_name || item.name || '—'}</td>
                                      <td className="py-1.5 pr-4 text-right font-mono">{item.quantity ?? '—'}</td>
                                      <td className="py-1.5 pr-4 text-right font-mono">
                                        {unitPrice != null ? `₹${Number(unitPrice).toFixed(2)}` : '—'}
                                      </td>
                                      <td className={`py-1.5 text-right font-mono font-semibold text-${itemAccent}-700`}>
                                        {item.item_total != null
                                          ? `₹${Number(item.item_total).toFixed(2)}`
                                          : item.total != null
                                          ? `₹${Number(item.total).toFixed(2)}`
                                          : '—'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination (only shown if data spans multiple pages) */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50 text-sm">
            <span className="text-slate-500">
              Page {page + 1} of {totalPages} · {total} total {cfg.countLabel}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                ← Prev
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                Next →
              </Button>
            </div>
          </div>
        )}
      </Card>
      </div>
    </div>
  );
};

export default TransactionLogContent;
